import React, { useCallback, useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { ChatCircleDots, X, PaperPlaneTilt } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function LiveChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [subject, setSubject] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const loadTickets = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/support/tickets/mine");
      setTickets(data);
      setActiveTicket((current) => (data.length > 0 && !current ? data[0] : current));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[chat] could not load tickets:", err);
    }
  }, [user]);

  useEffect(() => {
    if (open) loadTickets();
  }, [open, user, loadTickets]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [activeTicket]);

  const sendMessage = async () => {
    if (!msg.trim()) return;
    setLoading(true);
    try {
      if (activeTicket) {
        const { data } = await api.post(`/support/tickets/${activeTicket.ticket_id}/reply`, { text: msg });
        setActiveTicket(data);
        setTickets((prev) => prev.map((t) => (t.ticket_id === data.ticket_id ? data : t)));
      } else {
        const { data } = await api.post("/support/tickets", {
          subject: subject || "General enquiry",
          initial_message: msg,
        });
        setTickets((prev) => [data, ...prev]);
        setActiveTicket(data);
      }
      setMsg("");
      setSubject("");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[chat] sendMessage failed:", err);
    }
    setLoading(false);
  };

  return (
    <>
      <button
        data-testid="live-chat-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 md:right-6 z-40 w-14 h-14 bg-[#A94438] text-white rounded-full flex items-center justify-center shadow-[0_12px_30px_rgba(169,68,56,0.35)] hover:scale-110 active:scale-95 transition-transform"
        title="Live chat with support"
      >
        {open ? <X size={24} weight="bold" /> : <ChatCircleDots size={26} weight="duotone" />}
      </button>

      {open && (
        <div
          data-testid="live-chat-panel"
          className="fixed bottom-24 right-5 md:right-6 z-40 w-[90vw] max-w-sm h-[540px] bg-white border border-[#E5E2D9] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.12)] flex flex-col overflow-hidden"
        >
          <div className="p-4 bg-[#1A1A18] text-white">
            <p className="font-heading font-bold text-base">AfriPantry Support</p>
            <p className="text-xs text-white/70 mt-0.5">We typically reply within a few hours</p>
          </div>

          {!user ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <p className="text-sm text-[#5C5C54] mb-4">Sign in to start a support chat.</p>
              <Link to="/login" onClick={() => setOpen(false)} className="btn-primary text-xs">Sign in</Link>
            </div>
          ) : activeTicket ? (
            <>
              <div className="px-4 py-2 border-b border-[#E5E2D9] flex items-center justify-between bg-[#F9F7F3]">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#5C5C54]">Subject</p>
                  <p className="text-sm font-semibold truncate">{activeTicket.subject}</p>
                </div>
                <button
                  onClick={() => setActiveTicket(null)}
                  className="text-xs font-semibold text-[#A94438] hover:underline"
                  data-testid="new-chat-btn"
                >
                  New chat
                </button>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F9F7F3]">
                {activeTicket.messages.map((m, i) => (
                  <div
                    key={`${m.at || ""}-${i}`}
                    className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm ${m.sender === "user" ? "bg-[#A94438] text-white ml-auto rounded-br-sm" : "bg-white border border-[#E5E2D9] rounded-bl-sm"}`}
                  >
                    {m.text}
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-[#E5E2D9] flex items-center gap-2 bg-white">
                <input
                  data-testid="chat-message-input"
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 bg-[#F9F7F3] border border-[#E5E2D9] rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#A94438]"
                />
                <button
                  data-testid="chat-send-btn"
                  onClick={sendMessage}
                  disabled={loading || !msg.trim()}
                  className="w-10 h-10 rounded-full bg-[#A94438] text-white flex items-center justify-center disabled:opacity-50 hover:bg-[#8A372E] transition-all"
                >
                  <PaperPlaneTilt size={18} weight="fill" />
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col p-4">
              <p className="text-sm text-[#5C5C54] mb-3">Start a new conversation — we'll get back to you soon.</p>
              <input
                data-testid="chat-subject-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject (optional)"
                className="input-field mb-3 text-sm"
              />
              <textarea
                data-testid="chat-initial-input"
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="How can we help you today?"
                rows={4}
                className="input-field text-sm resize-none mb-3"
              />
              <button
                data-testid="chat-start-btn"
                onClick={sendMessage}
                disabled={loading || !msg.trim()}
                className="btn-primary text-sm mb-3 disabled:opacity-50"
              >
                {loading ? "Sending..." : "Start chat"}
              </button>

              {tickets.length > 0 && (
                <div className="mt-2 pt-4 border-t border-[#E5E2D9] flex-1 overflow-y-auto">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#5C5C54] mb-2">Past conversations</p>
                  {tickets.map((t) => (
                    <button
                      key={t.ticket_id}
                      onClick={() => setActiveTicket(t)}
                      className="w-full text-left p-3 rounded-xl hover:bg-[#F2EFE9] transition-all"
                    >
                      <p className="text-sm font-semibold truncate">{t.subject}</p>
                      <p className="text-xs text-[#5C5C54] capitalize">{t.status}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
